import { cn } from '@/lib/utils';

interface SkeletonProps {
  className?: string;
}

/**
 * Base skeleton primitive — a pulsing placeholder block.
 * Use the composed variants below for common patterns.
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-[var(--bg-secondary)]',
        className
      )}
    />
  );
}

/**
 * A single card-shaped skeleton (for project/client grid cards).
 */
export function SkeletonCard() {
  return (
    <div className="rounded-lg border border-[var(--border-color)] p-5 space-y-3">
      <div className="flex items-start justify-between">
        <Skeleton className="h-5 w-3/5" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-4/5" />
      <div className="flex items-center justify-between pt-2 border-t border-[var(--border-color)]">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  );
}

/**
 * A table-row-shaped skeleton (for leads, tickets, invoices list items).
 */
export function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 p-4">
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-4 w-16 rounded-full" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="text-right">
        <Skeleton className="h-4 w-16 ml-auto" />
      </div>
    </div>
  );
}

/**
 * Skeleton for the dashboard stat cards (4-column grid).
 */
export function SkeletonStatCard() {
  return (
    <div className="rounded-lg border border-[var(--border-color)] p-5 space-y-3">
      <div className="flex items-start justify-between">
        <Skeleton className="h-8 w-8 rounded-lg" />
        <Skeleton className="h-3 w-12" />
      </div>
      <Skeleton className="h-8 w-16" />
      <Skeleton className="h-3 w-24" />
    </div>
  );
}
